import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  from,
  InMemoryCache,
  NextLink,
  Operation,
} from '@apollo/client';
import { SentryLink } from 'apollo-link-sentry';
import DebounceLink from 'apollo-link-debounce';
import { RetryLink } from '@apollo/client/link/retry';

const buildHttpLink = (uri: string) => {
  return createHttpLink({
    uri: uri,
  });
};

export const forwarder = (
  operation: Operation,
  forward: NextLink,
  setToken: (token: string) => void
) => {
  return forward(operation).map((response) => {
    const context = operation.getContext();
    const {
      response: { headers },
    } = context;
    if (headers) {
      const authHeader = headers.get('x-whatis-token');
      if (authHeader) {
        const newToken = authHeader.split(' ')[1];
        setToken(newToken);
      }
    }
    return response;
  });
};

const retryLink = new RetryLink({
  delay: {
    initial: 100,
    max: 500,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error, _operation) => {
      return !!error;
    },
  },
});

const authLink = new ApolloLink((operation, forward) => {
  const token = localStorage.getItem('token');
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      authorization: `Bearer ${token}`,
    },
  }));

  return forwarder(operation, forward, (token) =>
    localStorage.setItem('token', token)
  );
});

const DEFAULT_DEBOUNCE_TIMEOUT = 10;

export const createClient = (uri: string) => {
  return new ApolloClient({
    link: from([
      new DebounceLink(DEFAULT_DEBOUNCE_TIMEOUT),
      authLink,
      retryLink,
      buildHttpLink(uri),
      new SentryLink(),
    ]),
    connectToDevTools: true,
    cache: new InMemoryCache({
      typePolicies: {
        Attachment: {
          keyFields: ['uuid'],
        },
        WhatisUser: {
          keyFields: ['uuid'],
        },
        Subscription: {
          keyFields: ['customer_id'],
        },
        Whatis: {
          keyFields: ['whatis_id'],
        },
        Team: {
          keyFields: ['uuid'],
        },
        Tag: {
          keyFields: ['uuid'],
        },
        Token: {
          keyFields: ['uuid'],
        },
      },
    }),
  });
};
